import { TaskFlowSchemaDto } from '@shared/services/apiService/apiService';

const SUPPORTED_PROPERTY_TYPES = new Set(['string', 'integer', 'number', 'boolean']);
const ROOT_KEYS = new Set(['properties', 'required']);
const PROPERTY_KEYS = new Set(['type', 'description']);

export type SchemaValidationError =
  | 'schemaJsonInvalid'
  | 'schemaRootInvalid'
  | 'schemaUnknownField'
  | 'schemaPropertiesRequired'
  | 'schemaPropertyNameInvalid'
  | 'schemaPropertyTypeRequired'
  | 'schemaPropertyTypeInvalid'
  | 'schemaPropertyDescriptionInvalid'
  | 'requiredNamesInvalid'
  | 'requiredNamesDuplicate'
  | 'requiredNameMissing';

export type SchemaValidationResult =
  | { valid: true; schema: TaskFlowSchemaDto }
  | { valid: false; error: SchemaValidationError };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const validateCollectDataSchema = (value: unknown): SchemaValidationResult => {
  if (!isRecord(value)) return { valid: false, error: 'schemaRootInvalid' };

  if (Object.keys(value).some((key) => !ROOT_KEYS.has(key))) {
    return { valid: false, error: 'schemaUnknownField' };
  }
  if (!isRecord(value.properties)) {
    return { valid: false, error: 'schemaPropertiesRequired' };
  }

  const properties: TaskFlowSchemaDto['properties'] = {};
  for (const [name, rawProperty] of Object.entries(value.properties)) {
    if (!name.trim() || !isRecord(rawProperty)) {
      return { valid: false, error: 'schemaPropertyNameInvalid' };
    }
    if (Object.keys(rawProperty).some((key) => !PROPERTY_KEYS.has(key))) {
      return { valid: false, error: 'schemaUnknownField' };
    }
    if (typeof rawProperty.type !== 'string') {
      return { valid: false, error: 'schemaPropertyTypeRequired' };
    }
    if (!SUPPORTED_PROPERTY_TYPES.has(rawProperty.type)) {
      return { valid: false, error: 'schemaPropertyTypeInvalid' };
    }
    if (rawProperty.description !== undefined && typeof rawProperty.description !== 'string') {
      return { valid: false, error: 'schemaPropertyDescriptionInvalid' };
    }

    properties[name] = {
      type: rawProperty.type as TaskFlowSchemaDto['properties'][string]['type'],
      ...(rawProperty.description === undefined ? {} : { description: rawProperty.description }),
    };
  }

  if (value.required === undefined) return { valid: true, schema: { properties } };
  if (!Array.isArray(value.required) || value.required.some((name) => typeof name !== 'string')) {
    return { valid: false, error: 'requiredNamesInvalid' };
  }
  if (new Set(value.required).size !== value.required.length) {
    return { valid: false, error: 'requiredNamesDuplicate' };
  }
  if (value.required.some((name) => !(name in properties))) {
    return { valid: false, error: 'requiredNameMissing' };
  }

  return { valid: true, schema: { properties, required: value.required } };
};

export const parseCollectDataSchema = (rawText: string): SchemaValidationResult => {
  try {
    return validateCollectDataSchema(JSON.parse(rawText));
  } catch {
    return { valid: false, error: 'schemaJsonInvalid' };
  }
};
